import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography, Paper, Container } from '@mui/material';
import { Refresh, Home, Warning } from '@mui/icons-material';
import { clearAllRetryFlags } from '../../utils/lazyWithRetry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  isolate?: boolean; // If true, only affects this boundary's children
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    retryCount: 0,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isChunkError = this.isChunkLoadError(error);
    const isVersionMismatch = error?.name === 'VersionMismatchError';

    // Log error to console
    console.error('ErrorBoundary caught an error:', {
      error: error.message,
      isChunkError,
      isVersionMismatch,
      componentStack: errorInfo.componentStack,
    });

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Report to error tracking service (Sentry is already configured)
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
          errorBoundary: {
            isChunkError,
            isVersionMismatch,
            retryCount: this.state.retryCount,
          },
        },
        tags: {
          errorBoundary: true,
          chunkError: isChunkError,
          versionMismatch: isVersionMismatch,
        },
      });
    }

    // Auto-reload once for chunk/version errors (stale deployment recovery)
    if (isChunkError || isVersionMismatch) {
      const key = 'chunk-reload';
      const lastReload = sessionStorage.getItem(key);
      const now = Date.now();
      if (!lastReload || (now - parseInt(lastReload)) > 10000) {
        sessionStorage.setItem(key, String(now));
        window.location.reload();
        return;
      }
    }
  }

  private isChunkLoadError = (error: Error): boolean => {
    return (
      error?.name === 'VersionMismatchError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('Load failed') ||
      error?.message?.includes('dynamically imported module') ||
      error?.name === 'ChunkLoadError'
    );
  }

  private isVersionMismatchError = (error: Error): boolean => {
    return error?.name === 'VersionMismatchError';
  }

  private handleReset = () => {
    const { onReset } = this.props;

    // Clear all retry flags when resetting
    clearAllRetryFlags();

    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));

    // Call custom reset handler if provided
    if (onReset) {
      onReset();
    }
  };

  private handleGoHome = () => {
    window.location.href = '/home';
  };

  private handleRefresh = () => {
    clearAllRetryFlags();
    window.location.reload();
  };

  public render() {
    const { hasError, error, retryCount } = this.state;
    const { children, fallback, isolate = false } = this.props;

    if (hasError) {
      if (fallback) {
        return <>{fallback}</>;
      }

      const isVersionMismatch = this.isVersionMismatchError(error!);
      const isChunkError = this.isChunkLoadError(error!);

      return (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Warning
                color={isVersionMismatch ? 'info' : 'warning'}
                sx={{ fontSize: 48, mb: 2 }}
              />
              <Typography variant="h5" gutterBottom>
                {isVersionMismatch
                  ? 'Update Available'
                  : isChunkError
                    ? 'Loading Error'
                    : 'Something went wrong'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {isVersionMismatch
                  ? 'A new version of Homepoint has been deployed. Please refresh the page to continue.'
                  : isChunkError
                    ? 'We had trouble loading this page. Please check your internet connection and try again.'
                    : 'An unexpected error occurred. Please try again.'}
              </Typography>
              {retryCount > 0 && !isVersionMismatch && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Retry attempt: {retryCount}
                </Typography>
              )}
            </Box>

            {process.env.NODE_ENV === 'development' && error && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  textAlign: 'left',
                }}
              >
                <Typography variant="caption" component="pre" sx={{ fontSize: 10 }}>
                  {error.message}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
              {(isVersionMismatch || isChunkError) ? (
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={this.handleRefresh}
                  sx={{ minWidth: 160 }}
                >
                  {isVersionMismatch ? 'Refresh Now' : 'Try Again'}
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={this.handleReset}
                    sx={{ minWidth: 120 }}
                  >
                    Try Again
                  </Button>

                  {!isolate && (
                    <Button
                      variant="outlined"
                      startIcon={<Home />}
                      onClick={this.handleGoHome}
                    >
                      Go Home
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Container>
      );
    }

    return children;
  }
}

export default ErrorBoundary;

// Declare Sentry on window for TypeScript
declare global {
  interface Window {
    Sentry?: {
      captureException: (error: Error, context?: any) => void;
    };
  }
}